import type { FC, RefObject } from "react";
import { useMemo } from "react";
import { useEffect, useRef } from "react";
import { Spotify } from "react-spotify-embed";
import YouTubePlayer from "react-youtube";

import { UrlPreview } from "@/components/app/post/url-preview";
import {
  useOptionalPostCacheContext,
  usePostCacheContext,
} from "@/contexts/post-cache-context";
import { usePostContext } from "@/contexts/post-context";
import { useScriptLoader } from "@/contexts/script-loader-context";
import { useOwnTheme } from "@/contexts/theme-context";
import { detectUrls } from "@/utils/detect-urls";
import {
  feedFreezeDebugLog,
  getFeedFreezeDebugDuration,
} from "@/utils/feed-freeze-debug";
import { replaceCodeBlockWithPlaceholder } from "@/utils/post-utils";

import { AdvancedTradingViewChartContainer } from "../chart/advanced-trading-view-chart-container";

type EmbedPlayerProps = {
  url: string | string[];
  disabledURLs?: string[];
  detectedUrls?: EmbedURLMetadata[];
};

type SiteComponentProps<T = object> = {
  url: string;
  themeToUse: "light" | "dark";
  customColor?: string | null;
  theme: string;
  containerRef: RefObject<HTMLDivElement | null>;
} & T;

const customEmbeds = {
  youtube: {
    regexp:
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com|youtu\.be)\/((embed|live)\/)?(?:watch\?v=|shorts\/)?(?<videoId>[a-zA-Z0-9_-]{11})/gim,
    Component: ({ videoId }: SiteComponentProps<{ videoId: string }>) => {
      return (
        <YouTubePlayer
          videoId={videoId}
          className="
            w-full max-w-fit rounded-xl border
            [&>iframe]:max-w-full [&>iframe]:rounded-xl
          "
        />
      );
    },
  },
  spotify: {
    regexp:
      /(?:https?:\/\/)?(?:www\.)?open\.spotify\.com\/(?<embed>embed\/)?(?<type>track|album|artist|playlist|episode)\/([a-zA-Z0-9]{22})/gim,
    Component: ({
      url,
      type,
      embed,
    }: SiteComponentProps<{ type: string; embed?: string }>) => {
      const height = /track|episode/.test(type) ? 80 : "auto";

      if (embed) {
        return (
          <iframe
            rel="noopener noreferrer"
            sandbox="allow-scripts allow-same-origin allow-forms"
            src={url}
            title="Spotify Embed"
            height={height}
            style={{ width: "100%", aspectRatio: "16 / 9" }}
            scrolling="no"
          />
        );
      }

      return <Spotify link={url} width="100%" height={height} scrolling="no" />;
    },
  },
  twitter: {
    regexp:
      /(?:https?:\/\/)?(?:www\.)?(twitter|x)\.com\/([a-zA-Z0-9_]+)(?<status>\/status\/([0-9]+))?/gim,
    Component: ({
      url,
      status,
      themeToUse,
      containerRef,
    }: SiteComponentProps<{ status?: string }>) => {
      const isScriptReady = useScriptLoader({
        src: "//platform.twitter.com/widgets.js",
      });

      useEffect(() => {
        if (!isScriptReady) return;

        // @ts-expect-error twitter api
        window?.twttr?.widgets?.load(containerRef.current);
      }, [isScriptReady]);

      return (
        <div className="contents">
          {status && (
            <blockquote
              // temp-eslint-disable-next-line tailwindcss/no-custom-classname
              className="twitter-tweet"
              data-dnt="true"
              data-theme={themeToUse}
              data-width={1200}
            >
              <a href={url.replace("x.com", "twitter.com")} />
            </blockquote>
          )}
          {!status && (
            <a
              // temp-eslint-disable-next-line tailwindcss/no-custom-classname
              className="twitter-timeline"
              data-height="500"
              data-dnt="true"
              data-theme={themeToUse}
              href={url.replace("x.com", "twitter.com")}
            />
          )}
        </div>
      );
    },
  },
  instagram: {
    regexp: /(?:https?:\/\/)?(?:www\.)?instagram\.com\/p\/([a-zA-Z0-9_-]+)/gim,
    Component: ({ url }: SiteComponentProps) => {
      const isScriptReady = useScriptLoader({
        src: "//platform.instagram.com/en_US/embeds.js",
      });

      useEffect(() => {
        if (!isScriptReady) return;

        // @ts-expect-error instagram api
        window?.instgrm?.Embeds?.process();
      }, [isScriptReady]);

      return (
        <div className="contents">
          {/* temp-eslint-disable-next-line tailwindcss/no-custom-classname */}
          <blockquote className="instagram-media" data-instgrm-version="7">
            <a href={url} />
          </blockquote>
        </div>
      );
    },
  },
  soundcloud: {
    regexp:
      /(?:https?:\/\/)?(?:www\.)?soundcloud\.com\/([a-zA-Z0-9_-]+)\/([a-zA-Z0-9_-]+)/gim,
    Component: ({ url, customColor, theme }: SiteComponentProps) => {
      const src = useMemo(() => {
        const apiUrl = new URL("https://w.soundcloud.com/player/");
        const color =
          customColor || colorByTheme[theme as keyof typeof colorByTheme];
        apiUrl.searchParams.append("url", url);
        apiUrl.searchParams.append("color", color);
        apiUrl.searchParams.append("auto_play", "false");
        apiUrl.searchParams.append("hide_related", "false");
        apiUrl.searchParams.append("show_comments", "true");
        apiUrl.searchParams.append("show_user", "true");
        apiUrl.searchParams.append("show_reposts", "false");
        apiUrl.searchParams.append("show_teaser", "true");
        apiUrl.searchParams.append("visual", "true");
        return apiUrl.toString();
      }, [url, customColor, theme]);

      return (
        <iframe
          width="100%"
          height={url.includes("sets") ? 450 : 150}
          allow="autoplay"
          className="border"
          rel="noopener noreferrer"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          src={src}
        />
      );
    },
  },
  tiktok: {
    regexp:
      /(?:https?:\/\/)?(?:www\.)?tiktok\.com\/@([a-zA-Z0-9_-]+)\/video\/(?<id>[0-9]+)/gim,
    Component: ({ id }: SiteComponentProps<{ id: string }>) => {
      return (
        <iframe
          height="400"
          width="100%"
          rel="noopener noreferrer"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          src={`https://www.tiktok.com/player/v1/${id}?&music_info=1&description=1`}
          allow="fullscreen"
        />
      );
    },
  },
  facebook: {
    regexp:
      /(?:https?:\/\/)?(?:www\.)?facebook\.com\/([a-zA-Z0-9_]+)\/videos\/([0-9]+)/gim,
    Component: ({ url, containerRef }: SiteComponentProps) => {
      const isScriptReady = useScriptLoader({
        src: "//connect.facebook.net/en_US/sdk.js#xfbml=1&version=v3.2",
      });

      useEffect(() => {
        if (!isScriptReady) return;

        // @ts-expect-error facebook api
        window?.FB?.XFBML?.parse(containerRef.current);
      }, [isScriptReady]);

      return (
        <div className="w-full">
          <div
            //temp-temp-eslint-disable-next-line tailwindcss/no-custom-classname
            className="fb-video"
            data-href={url}
            data-show-text="false"
            data-width="auto"
          >
            <div
              // temp-eslint-disable-next-line tailwindcss/no-custom-classname
              className="fb-xfbml-parse-ignore"
              // We must use inline styles, adding extra classes will make the
              // Facebook API fail xD
              style={{
                width: "100%",
                aspectRatio: "16 / 9",
              }}
            >
              <blockquote cite={url}>
                <a href={url} />
              </blockquote>
            </div>
          </div>
        </div>
      );
    },
  },
  twitch: {
    regexp:
      /(?:https?:\/\/)?(?:www\.)?twitch\.tv\/(?<user>[a-zA-Z0-9_]+)(\/(?<type>[a-zA-Z0-9_]+))?(\/(?<id>[a-zA-Z0-9-_]+))?/gim,
    Component: ({
      user,
      type = "channel",
      id = user,
    }: SiteComponentProps<{ user: string; type?: string; id?: string }>) => {
      const host = new URL(process.env.NEXT_PUBLIC_FOCUS_URI).host;
      const parent = host.includes(":") ? "localhost" : host;

      return (
        <iframe
          src={`https://player.twitch.tv/?${type}=${id}&parent=${parent}&muted=true&autoplay=false`}
          height="300"
          width="100%"
          rel="noopener noreferrer"
          sandbox="allow-scripts allow-same-origin allow-forms"
          className="rounded-xl border"
          allowFullScreen
        />
      );
    },
  },
  dexscreener: {
    regexp: /(?:https?:\/\/)?(?:www\.)?dexscreener\.com\/([^/]+)\/([^/?]+)/gim,
    Component: ({ url }: SiteComponentProps) => {
      const cleanUrl = url.replace(/[>]/g, ""); // Remove any ">" characters
      return (
        <iframe
          src={`${cleanUrl}?embed=1&theme=dark&trades=0&info=0`}
          style={{ width: "100%", height: "600px" }}
          title="DexScreener Chart"
          rel="noopener noreferrer"
          sandbox="allow-scripts allow-same-origin allow-forms"
          frameBorder="0"
        />
      );
    },
  },
  openfund: {
    regexp:
      /(?:https?:\/\/)?(?:www\.)?openfund\.com\/trade\/(?<ticker>[^/?]+)/gim,
    Component: ({ ticker }: SiteComponentProps<{ ticker: string }>) => {
      return <AdvancedTradingViewChartContainer username={ticker} />;
    },
  },
};

const colorByTheme = {
  dark: "#ffffff",
  light: "#000000",
  "paper-light": "#caaf38",
  "paper-dark": "#caaf38",
  "modern-dark": "#0062ff",
  "modern-light": "#0062ff",
};

const Player: FC<EmbedURLMetadata> = ({ url, type, groups }) => {
  const { Component } = (type && customEmbeds?.[type]) || {};
  const { theme = "dark", darkTheme, customColor } = useOwnTheme();
  const themeToUse = darkTheme ? "dark" : "light";
  const containerRef = useRef<HTMLDivElement>(null);
  const { post, repost, isPureRepost, postCachePrefix } = usePostContext();
  const postHash = isPureRepost ? repost?.postHash : post?.postHash;
  if (!postHash) {
    throw new Error("Post hash is missing.");
  }

  const { embedsHeight, postCacheKey, resizeObserver } = usePostCacheContext(
    postHash,
    postCachePrefix,
  );
  const height = embedsHeight?.[url] || 0;

  useEffect(() => {
    const node = containerRef.current;

    if (!node) return;

    resizeObserver.observe(node);

    return () => {
      resizeObserver.unobserve(node);
    };
  }, [resizeObserver]);

  if (!Component) {
    return <UrlPreview url={url} compact />;
  }

  // We need this extra wrapper, otherwise React will fail at some point
  // because these 3rd party APIs are changing the DOM, and React has
  // no clue about that.
  return (
    <div
      className="w-full"
      ref={containerRef}
      data-cache-key={postCacheKey}
      data-track="embeds"
      data-track-id={url}
      style={{
        minHeight: height,
      }}
    >
      {/*
      // @ts-expect-error hard to type */}
      <Component
        url={url}
        themeToUse={themeToUse}
        theme={theme}
        customColor={customColor}
        containerRef={containerRef}
        {...groups}
      />
    </div>
  );
};

export type EmbedURLMetadata = {
  url: string;
  type?: keyof typeof customEmbeds;
  groups?: Record<string, string>;
};

export const useDetectEmbedURLs = ({
  text,
  cache = true,
}: {
  text: string | string[];
  cache: boolean;
}) => {
  text = Array.isArray(text) ? text.join(" ") : text;
  const urlDetectionCache = useOptionalPostCacheContext()?.urlDetectionCache;

  return useMemo<EmbedURLMetadata[]>(() => {
    // Remove code blocks
    const [cleanText] = replaceCodeBlockWithPlaceholder(text);
    const cacheEntry = cache ? urlDetectionCache?.get(cleanText) : undefined;

    if (cacheEntry) {
      return cacheEntry;
    }

    const customEmbedEntries = Object.entries(customEmbeds);
    const startedAt = performance.now();

    const results = detectUrls(cleanText)
      // Filter those that are partially the same e.g. youtube.com/watch?v=1234 and youtube.com/watch?v=1234&list=1234
      // Filter out files
      // Filter out our URLs
      .filter(
        ({ url, file }, i, arr) =>
          !arr.find((u) => u.url !== url && u.url.includes(url)) &&
          !file &&
          !url.includes("focus.xyz"),
      )
      // Make unique by URL
      .filter((url, i, arr) => arr.findIndex((u) => u.url === url.url) === i)
      // Check if the URL matches any of the custom embeds
      .map(({ url, protocol }) => {
        for (const [key, { regexp }] of customEmbedEntries) {
          const matches = regexp.exec(url);
          regexp.lastIndex = 0;
          if (matches) {
            return {
              url: protocol ? url : `https://${url}`,
              type: key as keyof typeof customEmbeds,
              groups: matches.groups,
            };
          }
        }
        return {
          url: protocol ? url : `https://${url}`,
        };
      });

    const durationMs = getFeedFreezeDebugDuration(startedAt);

    if (durationMs > 16 || cleanText.length > 4000 || results.length > 0) {
      feedFreezeDebugLog(
        "embed",
        "detectUrls",
        {
          durationMs,
          resultCount: results.length,
          textLength: cleanText.length,
          urls: results.slice(0, 3).map(({ type, url }) => ({ type, url })),
        },
        {
          throttleKey: `embed-detect:${cleanText.length}:${results.length}`,
          throttleMs: 2_000,
        },
      );
    }

    if (cache && urlDetectionCache) {
      urlDetectionCache.set(cleanText, results);
    }

    return results;
  }, [text, urlDetectionCache, cache]);
};

const EmbedPlayer = ({
  url,
  disabledURLs = [],
  detectedUrls,
}: EmbedPlayerProps) => {
  const detectedUrlsFromText = useDetectEmbedURLs({
    text: detectedUrls ? "" : url,
    cache: true,
  });
  const urls = detectedUrls ?? detectedUrlsFromText;

  return urls.map((detectedUrl) =>
    disabledURLs.includes(detectedUrl.url) ? null : (
      <Player key={detectedUrl.url} {...detectedUrl} />
    ),
  );
};

export default EmbedPlayer;
